import { useState, useContext, useEffect } from 'react';

import { useSelector } from 'react-redux';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
  Spotlight,
  SpotlightManager,
  SpotlightTarget,
  SpotlightTransition,
} from '@atlaskit/onboarding';
import { RightSidebar } from '@atlaskit/page-layout';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { uncurlyUuid } from '@atlassian/bitkit-analytics';
import ConversationAssistant, {
  type ConversationAssistantConfig,
} from '@atlassian/conversation-assistant';
import {
  RovoMentionProvider,
  RovoProfilecardProvider,
} from '@atlassian/conversation-assistant-content-renderer';
import ConversationAssistantSitePicker from '@atlassian/conversation-assistant-site-picker';

import { CurrentDataContext } from 'src/app/current-data';
import { createMentionProvider } from 'src/components/editor/providers/mention-provider';
import { buildProfilecardProvider } from 'src/components/editor/providers/profilecard-provider';
import { ChatStateProps } from 'src/components/navigation/src/components/index-next';
import { ResizeProps } from 'src/components/sidebar';
import { useIntl } from 'src/hooks/intl';
import { usePref } from 'src/hooks/use-pref';
import { Site } from 'src/redux/jira/types';
import { getCurrentPullRequestId } from 'src/redux/pull-request/selectors';
import { getDefaultWorkspace } from 'src/selectors/global-selectors';

import Sidebar from '../containers/sidebar';

import messages from './chat-sidebar.i18n';

export const BitbucketConversationAssistantSidebar = ({
  chatState,
  setChatState,
  rovoEnabledSites,
  updateSitePref,
}: ChatStateProps & {
  rovoEnabledSites: Site[];
  updateSitePref: (pref?: string) => Promise<void>;
}) => {
  const intl = useIntl();
  const { workspace: currentWorkspace, repository } =
    useContext(CurrentDataContext);
  const defaultWorkspace = useSelector(getDefaultWorkspace);
  const workspace = currentWorkspace || defaultWorkspace;
  const { siteId: selectedCloudId } = chatState;
  const setCloudId = (cloudId: string) => {
    updateSitePref(cloudId);
    setChatState({
      siteId: cloudId,
    });
  };
  const onCloseChat = () => {
    setChatState({
      isActive: false,
    });
  };

  const pullrequestId = useSelector(getCurrentPullRequestId);
  const profilecardProvider: any = buildProfilecardProvider({ intl });
  const mentionProvider: any = createMentionProvider({
    owner: workspace?.slug || '',
    slug: repository?.slug || '',
    conversationsMentionContext: {
      repositoryWorkspaceUUID: uncurlyUuid(workspace?.uuid || ''),
      repositoryUUID: uncurlyUuid(repository?.uuid || '') || null,
      // Default isPrivateRepo to true if there's no current repository.
      // This will scope user recommendations to users in the workspace.
      isPrivateRepo: repository ? !!repository.is_private : true,
      pullrequestId,
    },
    intl,
  });

  const [
    rovoSitePickerSpotlightSeen,
    updateRovoSitePickerSpotlightPref,
    isRovoSitePickerSpotlightLoading,
  ] = usePref('rovo-chat-site-picker-spotlight');

  const [isSpotlightActive, setIsSpotlightActive] = useState<boolean>(false);

  useEffect(() => {
    if (
      !isRovoSitePickerSpotlightLoading &&
      rovoSitePickerSpotlightSeen === undefined
    ) {
      setIsSpotlightActive(true);
    }
  }, [isRovoSitePickerSpotlightLoading, rovoSitePickerSpotlightSeen]);

  const sitePicker =
    rovoEnabledSites.length > 1 ? (
      <SpotlightManager>
        <Box xcss={xcss({ marginBottom: 'space.300', width: '50%' })}>
          <SpotlightTarget name="rovo-assistant-site-picker">
            <ConversationAssistantSitePicker
              sites={rovoEnabledSites.map(site => ({
                siteUrl: site.cloudName,
                cloudId: site.cloudId,
              }))}
              selectedCloudId={selectedCloudId}
              setCloudId={cloudId => {
                setCloudId(cloudId);
              }}
            />
          </SpotlightTarget>
        </Box>

        <SpotlightTransition>
          {isSpotlightActive && (
            <Spotlight
              target="rovo-assistant-site-picker"
              key="rovo-assistant-site-picker"
              heading={intl.formatMessage(
                messages.rovoSitePickerSpotlightTitle
              )}
              targetRadius={3}
              dialogWidth={275}
              targetBgColor={token('elevation.surface')}
              dialogPlacement="bottom left"
              actions={[
                {
                  onClick: () => {
                    setIsSpotlightActive(false);
                    updateRovoSitePickerSpotlightPref('true');
                  },
                  text: intl.formatMessage(
                    messages.rovoSitePickerSpotlightDoneButton
                  ),
                },
              ]}
            >
              {intl.formatMessage(messages.rovoSitePickerSpotlightDescription)}
            </Spotlight>
          )}
        </SpotlightTransition>
      </SpotlightManager>
    ) : undefined;

  const config: ConversationAssistantConfig = {
    siteId: selectedCloudId,
    product: 'bitbucket',
    assistanceServiceConfig: {
      headers: {
        'X-Issynthetic-Traffic': 'false',
        'X-Cloudid': selectedCloudId,
        'X-Workspace-Id': uncurlyUuid(workspace?.uuid || ''),
      },
    },
  };

  if (!chatState.isEnabled || !selectedCloudId || !chatState.isActive) {
    return null;
  }

  const rovoConversationAssistant = (
    <RovoMentionProvider value={{ value: mentionProvider }}>
      <RovoProfilecardProvider value={{ value: profilecardProvider }}>
        <ConversationAssistant
          key={selectedCloudId}
          testId="bitbucket-conversation-assistant"
          sitePickerRender={sitePicker}
          onCloseChat={onCloseChat}
          config={config}
        />
      </RovoProfilecardProvider>
    </RovoMentionProvider>
  );

  const resizableBitbucketRovoChatBar = FeatureGates.checkGate(
    StatsigFeatureKeys.resizableBitbucketRovoChatBar
  );

  const sidebarExpandedWidth = 447;

  return (
    <Box
      xcss={xcss({
        position: 'relative',
        zIndex: 'navigation',
        backgroundColor: 'elevation.surface',
      })}
    >
      {!resizableBitbucketRovoChatBar && (
        <RightSidebar
          isFixed
          testId="rovo-right-sidebar"
          id="rovo-right-sidebar"
          skipLinkTitle="Rovo Chat"
          width={chatState.isActive ? sidebarExpandedWidth : 0}
        >
          {rovoConversationAssistant}
        </RightSidebar>
      )}
      {resizableBitbucketRovoChatBar && (
        <Sidebar
          onResize={function (_props: ResizeProps) {}}
          backgroundColor={token('elevation.surface')}
          contentOnly
          collapsedContent={[]}
          expandedContent={[]}
          expandedWidth={sidebarExpandedWidth}
          sidebarMinWidth={400}
          isCollapsed={false}
          isCollapsible={false}
        >
          {rovoConversationAssistant}
        </Sidebar>
      )}
    </Box>
  );
};
